import dayjs, { Dayjs } from 'dayjs';
import type { ActivityBoardPermissionItem } from 'library/types';

export type InclusiveReportRange = {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
};

type ValidRange = {
  start: Dayjs;
  endExclusive: Dayjs;
};

const toValidRange = ({ startDate, endDate }: InclusiveReportRange): ValidRange | null => {
  if (!startDate?.isValid() || !endDate?.isValid() || endDate.isBefore(startDate, 'day')) return null;

  return {
    start: startDate.startOf('day'),
    endExclusive: endDate.startOf('day').add(1, 'day'),
  };
};

const addYears = (years: Set<number>, start: Dayjs, endInclusive: Dayjs) => {
  for (let year = start.year(); year <= endInclusive.year(); year += 1) years.add(year);
};

/**
 * Includes report years plus the complete span of every overlapping permission.
 * The latter is needed because estimated hours are distributed over the full
 * permission before the result is clipped to the report range.
 */
export const getRequiredHolidayYears = (
  permissionItems: readonly ActivityBoardPermissionItem[],
  reportRanges: readonly InclusiveReportRange[]
): number[] => {
  const ranges = reportRanges.map(toValidRange).filter((range): range is ValidRange => range !== null);
  const years = new Set<number>();

  ranges.forEach((range) => addYears(years, range.start, range.endExclusive.subtract(1, 'millisecond')));

  permissionItems.forEach((permission) => {
    const permissionStart = dayjs(permission.startDate);
    const permissionEnd = dayjs(permission.endDate);

    if (!permissionStart.isValid() || !permissionEnd.isValid() || !permissionEnd.isAfter(permissionStart)) return;

    const overlapsReport = ranges.some(
      (range) => permissionStart.isBefore(range.endExclusive) && permissionEnd.isAfter(range.start)
    );
    if (!overlapsReport) return;

    addYears(years, permissionStart, permissionEnd.subtract(1, 'millisecond'));
  });

  return Array.from(years).sort((left, right) => left - right);
};
